/**
 * VarianceDetailDrawer — read-only "why did this drift?" surface.
 *
 * Opened from the Pay Run Variance pivot. Composes data already resolved
 * by `usePayRunViews` + `payRunRecords`; no new resolvers, no writers.
 *
 * Hard rules:
 *   • Pure presentation. No mutations, no actions inside the drawer
 *     (Release / Approve live on Ready / Approvals — not here).
 *   • Never references customer pricing or `contractValue`.
 *   • Footer link to /jobs/:id is the single escape hatch into operational
 *     context — the drawer itself stays in the financial frame.
 */
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import type { PayRunRow } from "@/data/finance/payRun/usePayRunViews";
import type { PayRunRecord } from "@/data/finance/payRun/recordPayRun";
import {
  DRIFT_KIND_LABEL,
  type LaborVarianceSignal,
  type PayRunReconciliation,
  type VarianceSeverity,
} from "@/data/finance/payRun/varianceAnalysis";

const SEVERITY_CLASS: Record<VarianceSeverity, string> = {
  none: "border-muted text-muted-foreground",
  info: "border-muted text-muted-foreground",
  warn: "border-warning text-warning",
  critical: "border-destructive text-destructive",
};

const PARTY_LABEL = {
  internal_crew: "Internal crew",
  external_contractor: "Contractor",
  external_subcontractor: "Subcontractor",
} as const;

function money(n: number | null | undefined): string {
  if (n == null) return "—";
  const sign = n < 0 ? "−" : "";
  return `${sign}$${Math.abs(n).toLocaleString()}`;
}

function pct(n: number | null | undefined): string {
  if (n == null) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${Math.round(n * 100)}%`;
}

export type VarianceDetailContext =
  | { kind: "labor"; row: PayRunRow; signal: LaborVarianceSignal }
  | {
      kind: "reconciliation";
      reconciliation: PayRunReconciliation;
      record: PayRunRecord | undefined;
      row: PayRunRow | undefined;
      customerName: string;
    }
  | { kind: "paid"; record: PayRunRecord; customerName: string };

export function VarianceDetailDrawer({
  open, onOpenChange, context,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  context: VarianceDetailContext | null;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        {context?.kind === "labor" && <LaborBody ctx={context} />}
        {context?.kind === "reconciliation" && <ReconciliationBody ctx={context} />}
        {context?.kind === "paid" && <PaidBody ctx={context} />}
      </SheetContent>
    </Sheet>
  );
}

function PaidBody({ ctx }: { ctx: Extract<VarianceDetailContext, { kind: "paid" }> }) {
  const { record, customerName } = ctx;
  return (
    <div className="space-y-5">
      <SheetHeader className="space-y-1">
        <SheetTitle className="text-base">{customerName}</SheetTitle>
        <SheetDescription className="flex items-center gap-2 text-[11px] flex-wrap">
          <span className="font-mono">{record.jobId}</span>
          <span>·</span>
          <span className="font-mono">{record.id}</span>
          <span>·</span>
          <span>{record.view.partyName ?? PARTY_LABEL[record.partyKind]}</span>
        </SheetDescription>
      </SheetHeader>

      <Section title="Release">
        <ThreeCol
          a={{ label: "Released at", value: format(new Date(record.releasedAt), "MMM d, yyyy h:mm a") }}
          b={{ label: "Released by", value: record.releasedBy }}
          c={{ label: "Net released", value: money(record.netReleased) }}
        />
      </Section>

      {record.ledgerLines.length > 0 && (
        <Section title="Ledger lines posted">
          <ul className="space-y-1.5">
            {record.ledgerLines.map((l, i) => (
              <li key={i} className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className="text-[10px]">{l.category}</Badge>
                  <span className="truncate text-muted-foreground" title={l.memo}>{l.memo}</span>
                </span>
                <span className="font-mono shrink-0 ml-2">{money(l.amountCents / 100)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <OpenJobFooter jobId={record.jobId} />
    </div>
  );
}

// ── Labor variance body ─────────────────────────────────────────────────

function LaborBody({ ctx }: { ctx: Extract<VarianceDetailContext, { kind: "labor" }> }) {
  const { row, signal } = ctx;
  const v = row.view;
  return (
    <div className="space-y-5">
      <SheetHeader className="space-y-1">
        <SheetTitle className="text-base">{row.job.customerName}</SheetTitle>
        <SheetDescription className="flex items-center gap-2 text-[11px]">
          <span className="font-mono">{row.job.id}</span>
          <span>·</span>
          <span>{v.partyName ?? PARTY_LABEL[v.partyKind]}</span>
          <Badge variant="outline" className={`text-[10px] ${SEVERITY_CLASS[signal.severity]}`}>
            {signal.severity}{signal.direction ? ` · ${signal.direction}` : ""}
          </Badge>
        </SheetDescription>
      </SheetHeader>

      <Section title="Labor — estimated vs actual">
        <ThreeCol
          a={{ label: "Estimated", value: money(signal.estimated) }}
          b={{ label: "Actual", value: money(signal.actual) }}
          c={{
            label: `Δ (${pct(signal.variancePct)})`,
            value: money(signal.variance),
            tone: signal.severity,
          }}
        />
        {!signal.comparable && (
          <p className="text-[11px] text-muted-foreground mt-2">
            Not comparable yet — one side missing. A Crew-Lead closeout or
            estimate is required to score this variance.
          </p>
        )}
      </Section>

      <Section title="Field adds">
        {v.fieldAdds.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No field adds submitted.</p>
        ) : (
          <ul className="space-y-1.5">
            {v.fieldAdds.map((f) => (
              <li key={f.id} className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-2 min-w-0">
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      f.status === "approved" ? "border-success text-success"
                      : f.status === "rejected" ? "border-muted text-muted-foreground line-through"
                      : "border-warning text-warning"
                    }`}
                  >
                    {f.status}
                  </Badge>
                  <span className="truncate" title={f.reason}>{f.reason}</span>
                </span>
                <span className="font-mono shrink-0 ml-2">{money(f.amount)}</span>
              </li>
            ))}
          </ul>
        )}
        <FooterRow
          a={`Approved · ${money(v.fieldAddApprovedTotal)}`}
          b={`Pending · ${money(v.fieldAddPendingTotal)}`}
        />
      </Section>

      <Section title="Holdback & deductions">
        <ThreeCol
          a={{ label: "Holdback held", value: money(v.holdbackHeld) }}
          b={{ label: "Callback deductions", value: money(v.callbackDeductionTotal) }}
          c={{ label: "Net releasable", value: money(v.netReleasable) }}
        />
      </Section>

      <OpenJobFooter jobId={row.job.id} />
    </div>
  );
}

// ── Reconciliation body ─────────────────────────────────────────────────

function ReconciliationBody({
  ctx,
}: {
  ctx: Extract<VarianceDetailContext, { kind: "reconciliation" }>;
}) {
  const { reconciliation: r, record, row, customerName } = ctx;
  return (
    <div className="space-y-5">
      <SheetHeader className="space-y-1">
        <SheetTitle className="text-base">{customerName}</SheetTitle>
        <SheetDescription className="flex items-center gap-2 text-[11px] flex-wrap">
          <span className="font-mono">{r.jobId}</span>
          <span>·</span>
          <span className="font-mono">{r.payRunId}</span>
          <Badge variant="outline" className={`text-[10px] ${SEVERITY_CLASS[r.severity]}`}>
            {r.severity}
          </Badge>
        </SheetDescription>
      </SheetHeader>

      <Section title="Released snapshot">
        {record ? (
          <ThreeCol
            a={{ label: "Released at", value: format(new Date(record.releasedAt), "MMM d, yyyy") }}
            b={{ label: "Released by", value: record.releasedBy }}
            c={{ label: "Net released", value: money(record.netReleased) }}
          />
        ) : (
          <p className="text-[11px] text-muted-foreground">Original pay-run record not found.</p>
        )}
      </Section>

      <Section title="Net drift">
        <ThreeCol
          a={{ label: "Released net", value: money(r.releasedNet) }}
          b={{ label: "Current net", value: money(r.currentNet) }}
          c={{ label: "Δ", value: money(r.netDelta), tone: r.severity }}
        />
      </Section>

      <Section title="Findings">
        {r.findings.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No findings — reconciled.</p>
        ) : (
          <ul className="space-y-1.5">
            {r.findings.map((f, i) => (
              <li key={i} className="flex items-center justify-between text-[11px]">
                <Badge variant="outline" className={`text-[10px] ${SEVERITY_CLASS[f.severity]}`}>
                  {DRIFT_KIND_LABEL[f.kind]}
                </Badge>
                <span className="font-mono">{money(f.deltaAmount)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {record && record.ledgerLines.length > 0 && (
        <Section title="Ledger lines posted at release">
          <ul className="space-y-1.5">
            {record.ledgerLines.map((l, i) => (
              <li key={i} className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className="text-[10px]">{l.category}</Badge>
                  <span className="truncate text-muted-foreground" title={l.memo}>{l.memo}</span>
                </span>
                <span className="font-mono shrink-0 ml-2">{money(l.amountCents / 100)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {row && (
        <Section title="Current pay run · for comparison">
          <ThreeCol
            a={{ label: "Base", value: money(row.view.baseAmount) }}
            b={{ label: "Approved adds", value: money(row.view.fieldAddApprovedTotal) }}
            c={{ label: "Holdback", value: money(row.view.holdbackHeld) }}
          />
        </Section>
      )}

      <OpenJobFooter jobId={r.jobId} />
    </div>
  );
}

// ── Small layout primitives ─────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">{title}</div>
      <div className="rounded-md border bg-card/40 p-3">{children}</div>
    </div>
  );
}

function ThreeCol({
  a, b, c,
}: {
  a: { label: string; value: string };
  b: { label: string; value: string };
  c: { label: string; value: string; tone?: VarianceSeverity };
}) {
  const toneClass =
    c.tone === "critical" ? "text-destructive font-semibold"
    : c.tone === "warn" ? "text-warning"
    : "text-foreground";
  return (
    <div className="grid grid-cols-3 gap-3 text-[11px]">
      <Cell label={a.label} value={a.value} />
      <Cell label={b.label} value={b.value} />
      <Cell label={c.label} value={c.value} className={toneClass} />
    </div>
  );
}

function Cell({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className={`font-mono tabular-nums ${className ?? "text-foreground"}`}>{value}</div>
    </div>
  );
}

function FooterRow({ a, b }: { a: string; b: string }) {
  return (
    <div className="mt-3 pt-2 border-t flex justify-between text-[10px] text-muted-foreground tabular-nums">
      <span>{a}</span>
      <span>{b}</span>
    </div>
  );
}

function OpenJobFooter({ jobId }: { jobId: string }) {
  return (
    <div className="pt-2 border-t flex justify-end">
      <Button asChild size="sm" variant="ghost">
        <Link to={`/jobs/${jobId}`} title="Open the full job — leaves the financial frame">
          Open job <ExternalLink className="h-3.5 w-3.5 ml-1" />
        </Link>
      </Button>
    </div>
  );
}
